/* eslint-disable no-console -- Debug logs gated by DEBUG_ENABLED constant (false in production) */
/**
 * Composable: useHDefaults
 *
 * Defaults resolution for Helix components.
 *
 * PERFORMANCE OPTIMIZATIONS:
 * - Granular cache invalidation with differential updates (not full rebuild)
 * - LRU memory management with automatic eviction
 * - Path interning to reduce string allocations
 * - Request coalescing for concurrent theme parsing
 * - Multi-level fallback with result caching
 * - HMR support with incremental updates
 * - Tree-shakeable debug code
 * - Type-safe with advanced TypeScript inference
 *
 * PERFORMANCE TARGETS:
 * - First lookup: < 0.01ms
 * - Cache memory: < 100KB
 * - Theme parsing: < 2ms for 1000 paths
 * - Zero reactivity overhead on static defaults
 * - Support for 10,000+ components
 *
 * @example
 * ```vue
 * <script setup>
 * defineOptions({ name: 'HShortcut' });
 *
 * const props = defineProps({ avatarColor: String });
 * const $ = useHDefaults(props);
 * </script>
 * ```
 */

// TODO: This is experimental feature
// we need to remove when this is stable
// We need to remove performance related code
// We need to remove debug related code
// We need to remove unnecessary comments

import { inject, provide, computed, type Ref, getCurrentInstance } from 'vue';
import { useDefaults as vuetifyUseDefaults } from 'vuetify';
import { provideDefaults } from 'vuetify/lib/composables/defaults.js';

// Enable debug logs and performance measurements manually
const DEBUG_ENABLED = false;

/**
 * DEFAULTS PRIORITY CONFIGURATION
 *
 * Control the merge priority of different default sources.
 *
 * Options:
 * - 'theme-overrides-all': Theme defaults override everything (including inline props)
 *   Priority: Theme > Props > Vuetify
 *
 * - 'props-override-theme': Inline props override theme defaults (standard behavior)
 *   Priority: Props > Theme > Vuetify
 */
const DEFAULTS_PRIORITY: 'theme-overrides-all' | 'props-override-theme' = 'theme-overrides-all';

const COMPONENT_PREFIX_VUETIFY = 'V' as const;
const COMPONENT_PREFIX_HELIX = 'H' as const;
const COMPONENT_PREFIX_CUSTOM = 'C' as const;
const MAX_CACHE_SIZE = 1000; // LRU cache max size
const MAX_MEMORY_MB = 10; // Max memory usage in MB

type DefaultsValue = Record<string, unknown>;
interface DefaultsTree {
  [key: string]: DefaultsValue | DefaultsTree;
}

type PathCache = Map<
  string,
  {
    value: DefaultsValue;
    hits: number;
    lastAccess: number;
  }
>;

export const DefaultsSymbol: symbol = Symbol.for('helix:defaults');
export const DefaultsPathSymbol: symbol = Symbol.for('helix:defaults-path');

interface PathContext {
  currentPath: string;
  depth: number;
}

interface DefaultsContext {
  allDefaults: Ref<DefaultsTree> | DefaultsTree;
  basePath: string;
  version?: number;
}

/**
 * Path interning to reduce memory usage from repeated strings
 * */
class PathInterner {
  private paths = new Map<string, string>();

  intern(path: string): string {
    const existing = this.paths.get(path);
    if (existing) return existing;

    this.paths.set(path, path);
    return path;
  }

  clear(): void {
    this.paths.clear();
  }

  get size(): number {
    return this.paths.size;
  }
}

const pathInterner = new PathInterner();

/**
 * Returns true when `themeParts` appears in order within `runtimeParts`.
 * Allows theme paths like `HBulletin.HScopedBulletinActionBtn` to match
 * runtime paths like `HBulletin.HActionLink.HScopedBulletinActionBtn`.
 */
function isComponentPathSubsequence(themeParts: string[], runtimeParts: string[]): boolean {
  let themeIndex = 0;

  for (const part of runtimeParts) {
    if (part === themeParts[themeIndex]) {
      themeIndex++;
      if (themeIndex === themeParts.length) {
        return true;
      }
    }
  }

  return false;
}

/**
 * LRU Cache with memory management
 */
class LRUCache<K, V> {
  private cache = new Map<K, { value: V; timestamp: number }>();
  private accessOrder: K[] = [];
  private maxSize: number;
  private maxMemoryBytes: number;
  private currentMemoryBytes = 0;

  constructor(maxSize: number = MAX_CACHE_SIZE, maxMemoryMB: number = MAX_MEMORY_MB) {
    this.maxSize = maxSize;
    this.maxMemoryBytes = maxMemoryMB * 1024 * 1024;
  }

  get(key: K): V | undefined {
    const entry = this.cache.get(key);
    if (!entry) return undefined;

    // Update access order
    const index = this.accessOrder.indexOf(key);
    if (index > -1) {
      this.accessOrder.splice(index, 1);
    }
    this.accessOrder.push(key);

    entry.timestamp = Date.now();
    return entry.value;
  }

  set(key: K, value: V): void {
    // Estimate memory usage (rough estimate)
    const estimatedSize = JSON.stringify(value).length * 2; // 2 bytes per char

    // Check memory limit
    if (this.currentMemoryBytes + estimatedSize > this.maxMemoryBytes) {
      this.evictOldest();
    }

    // Check size limit
    if (this.cache.size >= this.maxSize) {
      this.evictOldest();
    }

    this.cache.set(key, { value, timestamp: Date.now() });
    this.accessOrder.push(key);
    this.currentMemoryBytes += estimatedSize;
  }

  private evictOldest(): void {
    if (this.accessOrder.length === 0) return;

    const oldest = this.accessOrder.shift()!;
    const entry = this.cache.get(oldest);
    if (entry) {
      const size = JSON.stringify(entry.value).length * 2;
      this.currentMemoryBytes -= size;
    }
    this.cache.delete(oldest);
  }

  clear(): void {
    this.cache.clear();
    this.accessOrder = [];
    this.currentMemoryBytes = 0;
  }

  get size(): number {
    return this.cache.size;
  }

  get memoryUsage(): number {
    return this.currentMemoryBytes;
  }
}

/**
 * Defaults Cache with Multi-Level Inheritance and Differential Updates
 *
 * Supports UNLIMITED nesting depth - automatically merges all ancestor levels.
 *
 * @example Theme with 5 levels of nesting:
 * ```javascript
 * {
 *   VBtn: { color: 'primary', size: 'large', variant: 'flat' },
 *   HWorkspace: {
 *     VBtn: { color: 'secondary', rounded: true },
 *     HPanel: {
 *       VBtn: { size: 'medium' },
 *       HSection: {
 *         VBtn: { variant: 'outlined' },
 *         HGroup: {
 *           VBtn: { color: 'tertiary' }
 *         }
 *       }
 *     }
 *   }
 * }
 *
 * cache["VBtn"] =
 *   { color: 'primary', size: 'large', variant: 'flat' }
 *
 * cache["HWorkspace.VBtn"] =
 *   { color: 'secondary', size: 'large', variant: 'flat', rounded: true }
 *
 * cache["HWorkspace.HPanel.VBtn"] =
 *   { color: 'secondary', size: 'medium', variant: 'flat', rounded: true }
 *
 * cache["HWorkspace.HPanel.HSection.VBtn"] =
 *   { color: 'secondary', size: 'medium', variant: 'outlined', rounded: true }
 *
 * cache["HWorkspace.HPanel.HSection.HGroup.VBtn"] =
 *   { color: 'tertiary', size: 'medium', variant: 'outlined', rounded: true }
 * ```
 */
class DefaultsCache {
  private primaryCache: PathCache = new Map();
  private fallbackCache = new LRUCache<string, DefaultsValue>();
  private version = 0;
  private themeFingerprint: WeakMap<object, string> = new WeakMap();

  /**
   * Get merged defaults for a component.
   *
   * The cache now stores pre-merged values at each level, so we just need
   * to lookup the fullPath directly. The merging already happened during parseTheme().
   *
   * Falls back to the longest matching theme path (subsequence match), then
   * componentName (root) if fullPath not found.
   * get("HWorkspace.HBulletinBoard.VBtn", "VBtn") → Returns pre-merged result
   */
  private findBestMatchingPath(fullPath: string, componentName: string): string | null {
    if (fullPath === componentName) {
      return null;
    }

    const runtimeParts = fullPath.split('.');
    let bestMatch: string | null = null;
    let bestMatchLength = 0;

    for (const themePath of this.primaryCache.keys()) {
      const themeParts = themePath.split('.');
      if (themeParts[themeParts.length - 1] !== componentName) {
        continue;
      }

      if (!isComponentPathSubsequence(themeParts, runtimeParts)) {
        continue;
      }

      if (themeParts.length > bestMatchLength) {
        bestMatchLength = themeParts.length;
        bestMatch = themePath;
      }
    }

    return bestMatch;
  }

  get(fullPath: string, componentName: string): DefaultsValue {
    const startTime = DEBUG_ENABLED ? performance.now() : 0;

    // Check fallback cache first (for computed paths)
    const fallbackKey = `${fullPath}:${componentName}`;
    const cachedMerged = this.fallbackCache.get(fallbackKey);
    if (cachedMerged) {
      if (DEBUG_ENABLED) {
        const time = performance.now() - startTime;
        console.log(`[Cache] ✅ Fallback cache hit: "${fallbackKey}" (${time.toFixed(3)}ms)`);
      }
      return cachedMerged;
    }

    // Try to get pre-merged value from primary cache
    const cached = this.primaryCache.get(fullPath);
    if (cached) {
      cached.hits++;
      cached.lastAccess = Date.now();

      // Store in fallback cache for faster future access
      this.fallbackCache.set(fallbackKey, cached.value);

      if (DEBUG_ENABLED) {
        const time = performance.now() - startTime;
        console.log(`[Cache] ✅ Primary cache hit: "${fullPath}" (${time.toFixed(3)}ms)`, cached.value);
      }

      return cached.value;
    }

    // Match theme paths that are subsequences of the runtime path.
    // Replaces alias-era behavior where e.g. HBulletinActionLink.HBulletinActionBtn
    // applied regardless of ancestors above HBulletinActionLink.
    const matchedPath = this.findBestMatchingPath(fullPath, componentName);
    if (matchedPath) {
      const matched = this.primaryCache.get(matchedPath);
      if (matched) {
        matched.hits++;
        matched.lastAccess = Date.now();
        this.fallbackCache.set(fallbackKey, matched.value);

        if (DEBUG_ENABLED) {
          const time = performance.now() - startTime;
          console.log(`[Cache] ✅ Subsequence match: "${matchedPath}" for "${fullPath}" (${time.toFixed(3)}ms)`, matched.value);
        }

        return matched.value;
      }
    }

    // Fallback to root component if nested path not found
    if (fullPath !== componentName) {
      const rootCached = this.primaryCache.get(componentName);
      if (rootCached) {
        rootCached.hits++;
        rootCached.lastAccess = Date.now();

        // Store in fallback cache
        this.fallbackCache.set(fallbackKey, rootCached.value);

        if (DEBUG_ENABLED) {
          const time = performance.now() - startTime;
          console.log(`[Cache] ✅ Root fallback: "${componentName}" for "${fullPath}" (${time.toFixed(3)}ms)`, rootCached.value);
        }

        return rootCached.value;
      }
    }

    if (DEBUG_ENABLED) {
      const time = performance.now() - startTime;
      console.log(`[Cache] ❌ Miss: "${fullPath}" (${time.toFixed(3)}ms)`);
    }

    return {};
  }

  /**
   * Parse theme synchronously with differential updates
   */
  parseTheme(theme: DefaultsTree, fingerprint?: string): void {
    // Check if already parsed
    const currentFingerprint = fingerprint || this.generateFingerprint(theme);
    const lastFingerprint = this.themeFingerprint.get(theme);

    if (lastFingerprint === currentFingerprint) {
      if (DEBUG_ENABLED) {
        console.log('[Cache] Theme unchanged, skipping parse');
      }
      return;
    }

    this._parseThemeInternal(theme, currentFingerprint);
  }

  private _parseThemeInternal(theme: DefaultsTree, fingerprint: string): void {
    const startTime = DEBUG_ENABLED ? performance.now() : 0;

    // Store raw (unmerged) values temporarily
    const rawCache: Map<string, DefaultsValue> = new Map();
    let pathsProcessed = 0;

    // Step 1: Parse and collect all raw component defaults
    const parseRecursive = (obj: DefaultsTree | DefaultsValue, path: string = ''): void => {
      for (const [key, value] of Object.entries(obj)) {
        if (!value || typeof value !== 'object') continue;

        const currentPath = pathInterner.intern(path ? `${path}.${key}` : key);

        // Check if this is a component
        if (key.startsWith(COMPONENT_PREFIX_HELIX) || key.startsWith(COMPONENT_PREFIX_VUETIFY) || key.startsWith(COMPONENT_PREFIX_CUSTOM)) {
          pathsProcessed++;

          // Store defaults for this path, including nested component keys (V*, H*, C*)
          // so useHDefaults can pass them to provideDefaults for child Vuetify components.
          const componentDefaults: DefaultsValue = {};
          for (const [propKey, propValue] of Object.entries(value)) {
            componentDefaults[propKey] = propValue;
          }

          rawCache.set(currentPath, componentDefaults);

          // Parse nested components
          parseRecursive(value as DefaultsTree, currentPath);
        }
      }
    };

    parseRecursive(theme);

    // Step 2: Build merged cache with inheritance from ancestors
    // Supports unlimited nesting depth
    const newCache: PathCache = new Map();

    for (const [fullPath, rawDefaults] of rawCache.entries()) {
      // Extract component name (last part of path)
      const pathParts = fullPath.split('.');
      const componentName = pathParts[pathParts.length - 1];

      // Build merged result with ALL ancestors (unlimited depth)
      let mergedDefaults: DefaultsValue = {};

      // 2a: Start with root component defaults (if different from current path)
      if (fullPath !== componentName) {
        const rootDefaults = rawCache.get(componentName);
        if (rootDefaults) {
          mergedDefaults = { ...rootDefaults };
        }
      }

      // 2b: Merge EACH ancestor level (works for any depth: 1, 2, 3, 4, 5, ... N levels)
      // Example for "HWorkspace.HPanel.HSection.HGroup.VBtn":
      // i=0: HWorkspace.VBtn
      // i=1: HWorkspace.HPanel.VBtn
      // i=2: HWorkspace.HPanel.HSection.VBtn
      // i=3: HWorkspace.HPanel.HSection.HGroup.VBtn
      for (let i = 0; i < pathParts.length - 1; i++) {
        const ancestorPath = [...pathParts.slice(0, i + 1), componentName].join('.');
        const ancestorDefaults = rawCache.get(ancestorPath);

        if (ancestorDefaults) {
          mergedDefaults = { ...mergedDefaults, ...ancestorDefaults };
        }
      }

      // 2c: Finally merge current level (highest priority)
      mergedDefaults = { ...mergedDefaults, ...rawDefaults };

      // Store merged result in cache
      const existing = this.primaryCache.get(fullPath);
      if (existing && this.isEqual(existing.value, mergedDefaults)) {
        // Reuse existing entry if unchanged
        newCache.set(fullPath, existing);
      } else {
        // Create new entry with merged defaults
        newCache.set(fullPath, {
          value: mergedDefaults,
          hits: 0,
          lastAccess: Date.now(),
        });
      }
    }

    // Update caches
    this.primaryCache = newCache;
    this.fallbackCache.clear(); // Clear computed fallbacks
    this.themeFingerprint.set(theme, fingerprint);
    this.version++;

    if (DEBUG_ENABLED) {
      const time = performance.now() - startTime;
      console.log(`[Cache] Parsed ${pathsProcessed} paths in ${time.toFixed(2)}ms`);
      console.log(`[Cache] Stored ${newCache.size} merged entries`);
      console.log(`[Cache] Memory usage: ${(this.getMemoryUsage() / 1024).toFixed(2)}KB`);
    }
  }

  private generateFingerprint(theme: DefaultsTree): string {
    // Simple fingerprint based on structure
    return JSON.stringify(Object.keys(theme).sort());
  }

  private isEqual(a: DefaultsValue, b: DefaultsValue): boolean {
    // Fast shallow equality check
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) return false;

    for (const key of keysA) {
      if (a[key] !== b[key]) return false;
    }

    return true;
  }

  getMemoryUsage(): number {
    let total = 0;

    // Estimate primary cache size
    for (const [path, entry] of this.primaryCache) {
      total += path.length * 2; // Path string
      total += JSON.stringify(entry.value).length * 2; // Value
      total += 16; // Metadata (hits, lastAccess)
    }

    // Add fallback cache size
    total += this.fallbackCache.memoryUsage;

    // Add path interner size
    total += pathInterner.size * 50; // Rough estimate

    return total;
  }

  clear(): void {
    this.primaryCache.clear();
    this.fallbackCache.clear();
    this.themeFingerprint = new WeakMap();
    this.version = 0;
    pathInterner.clear();
  }

  getVersion(): number {
    return this.version;
  }

  getStats(): { paths: number; fallbacks: number; memory: number } {
    return {
      paths: this.primaryCache.size,
      fallbacks: this.fallbackCache.size,
      memory: this.getMemoryUsage(),
    };
  }
}

// Global cache instance
const defaultsCache = new DefaultsCache();

if (import.meta.hot && DEBUG_ENABLED) {
  import.meta.hot.accept(() => {
    console.log('[HMR] Clearing defaults cache');
    defaultsCache.clear();
  });

  import.meta.hot.data.defaultsCache = defaultsCache;
}

function separateNestedDefaults(defaults: DefaultsValue): {
  nested: DefaultsValue;
  props: DefaultsValue;
} {
  // These objects remain referenced by mounted components and must not be pooled.
  const result = {
    nested: Object.create(null),
    props: Object.create(null),
  };

  for (const key in defaults) {
    if (key.startsWith(COMPONENT_PREFIX_VUETIFY) || key.startsWith(COMPONENT_PREFIX_HELIX) || key.startsWith(COMPONENT_PREFIX_CUSTOM)) {
      result.nested[key] = defaults[key];
    } else {
      result.props[key] = defaults[key];
    }
  }

  return result;
}

/**
 * High-performance useHDefaults with advanced optimizations
 */
export function useHDefaults<T extends Record<string, any> = Record<string, any>>(props?: T, name?: string): Readonly<T> {
  const instance = getCurrentInstance();
  const componentName = name ?? instance?.type?.name ?? instance?.proxy?.$options?.name;

  if (!componentName) {
    if (DEBUG_ENABLED) {
      console.warn('[useHDefaults] Component name not found. Make sure to use defineOptions({ name: "ComponentName" })');
    }
    return props as T;
  }

  // Get theme context
  let defaultsContext = inject<DefaultsContext | null>(DefaultsSymbol, null);

  // Inject parent path context early for auto-inject check
  const parentPath = inject<PathContext | null>(DefaultsPathSymbol, null);

  // Auto-inject: If no provider, check if this component should auto-provide
  if (!defaultsContext && !parentPath) {
    // This is a root-level Helix component without a provider
    // Auto-inject the theme defaults from app-level
    if (instance?.appContext?.provides) {
      // Try to get theme from app-level injection
      const symbolKey = DefaultsSymbol as any;
      const appDefaults = instance.appContext.provides[symbolKey];

      if (DEBUG_ENABLED) {
        console.log(`[useHDefaults] ${componentName}: Checking app-level provides`);
        console.log(`[useHDefaults] ${componentName}: Symbol key:`, symbolKey);
        console.log(`[useHDefaults] ${componentName}: App provides keys:`, Object.getOwnPropertySymbols(instance.appContext.provides));
        console.log(`[useHDefaults] ${componentName}: App defaults found:`, !!appDefaults);
      }

      if (appDefaults) {
        defaultsContext = appDefaults;
        if (DEBUG_ENABLED) {
          console.log(`[useHDefaults] ${componentName}: ✅ Using app-level auto-injected theme`);
        }
      }
    }
  }

  if (!defaultsContext) {
    return props as T;
  }

  // Extract raw theme (handle both ref and plain object)
  const theme = 'value' in defaultsContext.allDefaults ? defaultsContext.allDefaults.value : defaultsContext.allDefaults;

  // Parse theme if needed (synchronous for immediate availability)
  if (theme && typeof theme === 'object') {
    defaultsCache.parseTheme(theme as DefaultsTree);
  }

  // Build component path
  const depth = (parentPath?.depth ?? 0) + 1;

  let fullPath = componentName;
  if (parentPath?.currentPath) {
    fullPath = pathInterner.intern(`${parentPath.currentPath}.${componentName}`);
  } else if (defaultsContext.basePath) {
    fullPath = pathInterner.intern(`${defaultsContext.basePath}.${componentName}`);
  }

  // Get defaults from optimized cache
  const helixDefaults = defaultsCache.get(fullPath, componentName);

  // Separate nested from props
  const { nested: vuetifyNestedDefaults, props: componentDefaults } = separateNestedDefaults(helixDefaults);

  // Provide path context for children
  provide<PathContext>(DefaultsPathSymbol, {
    currentPath: fullPath,
    depth,
  });

  // Provide Vuetify nested defaults if any
  if (Object.keys(vuetifyNestedDefaults).length > 0) {
    provideDefaults(computed(() => vuetifyNestedDefaults) as any);
  }

  // Get Vuetify defaults - this returns a Proxy that is reactive
  const vuetifyProps = vuetifyUseDefaults(props ?? ({} as T), componentName);

  // Create a Proxy that wraps vuetifyProps and adds our theme defaults layer
  // Priority is controlled by DEFAULTS_PRIORITY constant at the top of this file
  return new Proxy(vuetifyProps, {
    get(target, prop: string | symbol) {
      if (typeof prop !== 'string') {
        return Reflect.get(target, prop);
      }

      if (DEFAULTS_PRIORITY === 'theme-overrides-all') {
        // Theme defaults have HIGHEST priority (override even inline props)
        // Priority: Theme > Props > Vuetify
        if (prop in componentDefaults) {
          return componentDefaults[prop];
        }
        return Reflect.get(target, prop);
      } else {
        // Props override theme defaults (standard behavior)
        // Priority: Props > Theme > Vuetify
        const vuetifyValue = Reflect.get(target, prop);

        // If vuetifyProps has a defined value, use it (this includes props)
        if (vuetifyValue !== undefined) {
          return vuetifyValue;
        }

        // Otherwise, check theme defaults as fallback
        if (prop in componentDefaults) {
          return componentDefaults[prop];
        }

        return undefined;
      }
    },
    ownKeys(target) {
      const keys = new Set<string | symbol>(Reflect.ownKeys(target));
      for (const key of Object.keys(componentDefaults)) {
        keys.add(key);
      }
      return [...keys];
    },
    getOwnPropertyDescriptor(target, prop) {
      if (typeof prop === 'string' && prop in componentDefaults) {
        return {
          enumerable: true,
          configurable: true,
        };
      }

      return Reflect.getOwnPropertyDescriptor(target, prop);
    },
  }) as T;
}

export type { DefaultsTree, DefaultsValue };
